import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";

const ROLE_OPTS = [
  { v: "employee", l: "Colaborador" },
  { v: "manager", l: "Gestor" },
  { v: "company_admin", l: "Admin Empresa" },
];

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", position_id: "", hire_date: "" });

  const refresh = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/positions")]);
    setItems(u.data); setPositions(p.data);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    try {
      await api.post("/users", { ...form, position_id: form.position_id || null, hire_date: form.hire_date || null });
      toast.success("Colaborador adicionado");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "employee", position_id: "", hire_date: "" });
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remover este colaborador?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Removido"); refresh();
  };

  return (
    <div className="space-y-8" data-testid="admin-users-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Gestão</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Colaboradores</h1>
          <p className="text-slate-600 mt-2 text-sm">Cadastre e gerencie a equipe da sua empresa.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-navy" data-testid="user-new-btn"><Plus className="w-4 h-4 mr-2" />Novo</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo colaborador</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label>Senha</Label><Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Perfil</Label>
                  <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                    {ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                  </select>
                </div>
                <div><Label>Cargo</Label>
                  <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={form.position_id} onChange={(e) => setForm({ ...form, position_id: e.target.value })}>
                    <option value="">—</option>
                    {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Data de admissão</Label>
                <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="user-hire-date" />
                <p className="text-[11px] text-slate-500 mt-1">Usada para liberar cursos da trilha ao longo do tempo (ex.: LGPD após 90 dias).</p>
              </div>
              <Button className="btn-navy w-full" onClick={save} data-testid="user-save-btn">Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Nome</th><th className="px-4 py-3">E-mail</th><th className="px-4 py-3">Perfil</th><th className="px-4 py-3">Cargo</th><th className="px-4 py-3 text-right">Ações</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500"><Users className="w-8 h-8 mx-auto mb-2 text-slate-400" />Sem colaboradores cadastrados.</td></tr>}
            {items.map((u) => (
              <tr key={u.id} className="border-t border-[var(--unigr-border)]">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><Badge variant="outline">{u.role}</Badge></td>
                <td className="px-4 py-3">{positions.find((p) => p.id === u.position_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`user-del-${u.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
